import {
  BankAdditionalRoles,
  CalculationStrategy,
  ClientKind,
  Concept,
  ConservationState,
  convertBankAdditionalRoles,
  convertCalculationStrategy,
  convertClientKind,
  convertConcept,
  convertConservationState,
  convertCriteriaGroupActivity,
  convertDocumentCategory,
  convertEvaluationType,
  convertEvaluationTypeBank,
  convertInspectionType,
  convertPaymentStatus,
  convertProfileUsers,
  convertPropertyType,
  convertPropertyUse,
  convertQuestionType,
  convertRegistrationUf,
  convertReportFunction,
  convertReportGoal,
  convertReportMode,
  convertStandard,
  convertStandard400K,
  convertStatus,
  convertYesOrNo,
  EvaluationType,
  EvaluationTypeBank,
  InspectionType,
  NoticeCriteriaGroupActivity,
  PaymentStatus,
  ProfileUsers,
  PropertyType,
  PropertyUse,
  QuestionType,
  RegistrationUf,
  ReportFunction,
  ReportGoal,
  ReportMode,
  SelectDocumentCategory,
  Standard,
  Status,
  YesOrNo,
} from '../api/enumerations';
import {
  AreaType,
  ArtRrt,
  convertAreaType,
  convertArtRrt,
  convertMethod,
  convertModality,
  convertObjective,
  convertPropertyType as convertPreFlightPropertyType,
  Method,
  Modality,
  Objective,
  PreFlightPropertyType,
} from '../api/preFlight/enumerations';

export const independentStructuresGroup = new Set<PropertyType>([
  PropertyType.SHED,
  PropertyType.STORE,
  PropertyType.HOUSE,
  PropertyType.COMMERCIAL_BUILDING,
]);

export const privateUnitsGroup = new Set<PropertyType>([
  PropertyType.APARTMENT,
  PropertyType.COMMERCIAL_SPACE,
]);

export const parcelGroup = new Set<PropertyType>([PropertyType.LOT]);

export interface SelectProps {
  value: number;
  label: string;
}

export interface StringSelectProps {
  value: string;
  label: string;
}

export const selectEvaluationType = (isHiveOwner: boolean): SelectProps[] => {
  const availableTypes = Object.values(EvaluationType)
    .filter((v) => !Number.isNaN(Number(v)))
    .map(Number)
    .filter((type) => {
      if (isHiveOwner) {
        return (
          type !== EvaluationType.AUTOFACTORS &&
          type !== EvaluationType.AUTOINFERENCES
        );
      }
      return (
        type !== EvaluationType.COMPLETEFACTORS &&
        type !== EvaluationType.COMPLETEINFERENCES
      );
    });

  return availableTypes.map((value) => ({
    value,
    label: convertEvaluationType(value as EvaluationType),
  }));
};

export const selectEvaluationTypeBank = (): SelectProps[] => {
  const enumValues = Object.values(EvaluationTypeBank).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: EvaluationTypeBank = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertEvaluationTypeBank(enumKey),
    };
  });
};

export const selectClientKind = (): SelectProps[] => {
  const enumValues = Object.values(ClientKind).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: ClientKind = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertClientKind(enumKey),
    };
  });
};

/**
 * Perfis atribuíveis a um usuário do hive (RealPrice). O enum ProfileUsers
 * agora espelha todo o `Role` canônico (inclui papéis de banco/cliente), mas
 * este dropdown só oferece os papéis do hive; os de banco têm o
 * `selectBankProfileUser`.
 */
const ASSIGNABLE_PROFILE_USERS: ProfileUsers[] = [
  ProfileUsers.SELECT,
  ProfileUsers.ARCHITECT,
  ProfileUsers.ENGINEER,
  ProfileUsers.ADMIN,
  ProfileUsers.FINANCEIRO,
  ProfileUsers.JURIDICO,
  ProfileUsers.CS,
];

export const selectProfileUser = (): SelectProps[] =>
  ASSIGNABLE_PROFILE_USERS.map((value) => ({
    value,
    label: convertProfileUsers(value),
  }));

export const selectBankProfileUser = (): SelectProps[] => {
  const enumValues = Object.values(BankAdditionalRoles).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: BankAdditionalRoles = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertBankAdditionalRoles(enumKey),
    };
  });
};

export const selectPropertyType = (): SelectProps[] => {
  let enumValues = Object.values(PropertyType).filter(
    (v) => !Number.isNaN(Number(v))
  );

  enumValues = enumValues.filter((v) => Number(v) !== PropertyType.LOT);

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: PropertyType = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertPropertyType(enumKey),
    };
  });
};

export const selectPropertyUse = (): SelectProps[] => {
  const enumValues = Object.values(PropertyUse).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: PropertyUse = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertPropertyUse(enumKey),
    };
  });
};

export const selectQuestionType = (): SelectProps[] => {
  const enumValues = Object.values(QuestionType).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: QuestionType = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertQuestionType(enumKey),
    };
  });
};

export const selectRegistrationUf = (): SelectProps[] => {
  const enumValues = Object.values(RegistrationUf).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: RegistrationUf = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertRegistrationUf(enumKey),
    };
  });
};

export const selectReportFunction = (): SelectProps[] => {
  const enumValues = Object.values(ReportFunction).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: ReportFunction = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertReportFunction(enumKey),
    };
  });
};

export const selectReportGoal = (): SelectProps[] => {
  const enumValues = Object.values(ReportGoal).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: ReportGoal = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertReportGoal(enumKey),
    };
  });
};

export const selectReportMode = (): SelectProps[] => {
  const enumValues = Object.values(ReportMode).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: ReportMode = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertReportMode(enumKey),
    };
  });
};

export const selectCalculationStrategy = (): SelectProps[] => {
  const enumValues = Object.values(CalculationStrategy).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: CalculationStrategy = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertCalculationStrategy(enumKey),
    };
  });
};

export const selectConcept = (): SelectProps[] => {
  const enumValues = Object.values(Concept).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: Concept = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertConcept(enumKey),
    };
  });
};

export const selectConservation = (): SelectProps[] => {
  const enumValues = Object.values(ConservationState).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: ConservationState = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertConservationState(enumKey),
    };
  });
};

export const selectStandard = (): SelectProps[] => {
  const enumValues = Object.values(Standard).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: Standard = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertStandard(enumKey),
    };
  });
};

export const selectStandard400K = (): SelectProps[] => {
  const enumValues = Object.values(Standard).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: Standard = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertStandard400K(enumKey),
    };
  });
};

export const selectStatus = (): SelectProps[] => {
  const enumValues = Object.values(Status).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: Status = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertStatus(enumKey),
    };
  });
};

export const selectYesOrNo = (): SelectProps[] => {
  const enumValues = Object.values(YesOrNo).filter(
    (v) => !Number.isNaN(Number(v))
  );
  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: YesOrNo = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertYesOrNo(enumKey),
    };
  });
};

export const selectInspectionType = (
  includeNoInspection = true
): SelectProps[] => {
  const enumValues = Object.values(InspectionType)
    .filter((value) => !Number.isNaN(Number(value)))
    .map(Number)
    .filter(
      (value) =>
        includeNoInspection ||
        (value !== InspectionType.NO_INSPECTION &&
          value !== InspectionType.REMOTE)
    );

  return enumValues.map((value) => ({
    value,
    label: convertInspectionType(value as InspectionType),
  }));
};

export const selectDocumentCategory = (): SelectProps[] => {
  const enumValues = Object.values(SelectDocumentCategory).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: SelectDocumentCategory = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertDocumentCategory(enumKey),
    };
  });
};

export const selectGroupActivity = (): SelectProps[] => {
  const enumValues = Object.values(NoticeCriteriaGroupActivity).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: NoticeCriteriaGroupActivity = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertCriteriaGroupActivity(enumKey),
    };
  });
};

export const selectPreFlightObjective = (): StringSelectProps[] =>
  Object.values(Objective).map((value) => ({
    value,
    label: convertObjective(value),
  }));

export const selectPreFlightPropertyType = (): StringSelectProps[] =>
  Object.values(PreFlightPropertyType).map((value) => ({
    value,
    label: convertPreFlightPropertyType(value),
  }));

export const selectPreFlightAreaType = (): StringSelectProps[] =>
  Object.values(AreaType).map((value) => ({
    value,
    label: convertAreaType(value),
  }));

export const selectPreFlightModality = (): StringSelectProps[] =>
  Object.values(Modality).map((value) => ({
    value,
    label: convertModality(value),
  }));

export const selectPreFlightMethod = (): StringSelectProps[] =>
  Object.values(Method).map((value) => ({
    value,
    label: convertMethod(value),
  }));

export const selectPreFlightArtRrt = (): StringSelectProps[] =>
  Object.values(ArtRrt).map((value) => ({
    value,
    label: convertArtRrt(value),
  }));

export const selectPaymentStatus = (): SelectProps[] => {
  const enumValues = Object.values(PaymentStatus).filter(
    (v) => !Number.isNaN(Number(v))
  );

  return enumValues.map((value) => {
    const valueAsNumber = Number(value);
    const enumKey: PaymentStatus = valueAsNumber;
    return {
      value: valueAsNumber,
      label: convertPaymentStatus(enumKey),
    };
  });
};
